import React, { useEffect, useState, useCallback } from 'react'
import { get } from 'lodash'
import { useHistory } from 'react-router-dom'
import { gtmEvent } from 'lib/gtm'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import Avatar from '@material-ui/core/Avatar'
import CardMedia from '@material-ui/core/CardMedia'
import CardActions from '@material-ui/core/CardActions'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import ConfirmationNumberOutlinedIcon from '@material-ui/icons/ConfirmationNumberOutlined'
import { IconInfoActive } from 'components/atoms/icons'
import useIconAccessor from 'components/lesson-type-category/icons'
import { useStyles } from './styles'
import { defineMinimumPricePerLesson } from 'utils/defineMinimumPricePerLesson'
import BookingDetails from './booking-details-dialog'
import VerifyAccessCode from '../verify-access-code'
import ChipsSection from './chips-section'
import Hidden from '@material-ui/core/Hidden'

export default ({
  lessonInfo,
  bookingUrl,
  shouldBeClickable,
  coachName,
  coachExternalId,
  isSingleSession,
  lessonCreditBalance,
  coachIsViewingOwnSchedule,
}) => {
  const {
    id,
    title,
    durationInMinutes,
    priceInCents,
    locations,
    category = 'OTHER',
    status,
    lessonPacks = [],
    requiresAccessCode,
  } = lessonInfo

  const classes = useStyles({ shouldBeClickable })
  const [openDetails, setOpenDetails] = useState(false)
  const [openLessonPackDetails, setOpenLessonPackDetails] = useState(false)
  const [openVerifyAccessCode, setOpenVerifyAccessCode] = useState(false)
  const [verifiedAccessCode, setVerifiedAccessCode] = useState(false)
  const history = useHistory()
  const accessor = useIconAccessor()

  const lessonCreditBalanceQuantity = get(lessonCreditBalance, '0.quantity')
  const nextExpirationDate = get(lessonCreditBalance, '0.nextExpirationDate')
  const withLessonPacksCard = lessonPacks.length > 0

  const lessonPacksPrice = defineMinimumPricePerLesson(lessonPacks)

  const handleDetailsClick = e => {
    e.preventDefault()
    e.stopPropagation()
    withLessonPacksCard ? setOpenLessonPackDetails(true) : setOpenDetails(true)
  }

  const handleCardClick = useCallback(
    e => {
      if (!shouldBeClickable) return
      if (
        requiresAccessCode &&
        !verifiedAccessCode &&
        !lessonCreditBalanceQuantity
      ) {
        setOpenVerifyAccessCode(true)
        return
      }
      let redirectUrl =
        isSingleSession || coachIsViewingOwnSchedule
          ? bookingUrl
          : `${bookingUrl}&isLessonPacks=true`
      redirectUrl = lessonCreditBalanceQuantity
        ? `${redirectUrl}&lessonCreditBalanceQuantity=${lessonCreditBalanceQuantity}`
        : redirectUrl
      history.push(redirectUrl, { verifiedAccessCode: true })

      gtmEvent({
        event: 'formSubmit',
        formCategory: 'book-session',
        formAction: 'selected-booking-type',
        metaData: {
          ...lessonInfo,
        },
      })
    },
    [
      bookingUrl,
      history,
      isSingleSession,
      lessonCreditBalanceQuantity,
      lessonInfo,
      requiresAccessCode,
      shouldBeClickable,
      verifiedAccessCode,
      coachIsViewingOwnSchedule,
    ],
  )

  useEffect(() => {
    verifiedAccessCode && handleCardClick()
  }, [verifiedAccessCode, handleCardClick])

  const showLessonPackPrice =
    (!isSingleSession && withLessonPacksCard) ||
    (isSingleSession && !lessonCreditBalanceQuantity && lessonPacksPrice)

  return (
    <>
      <Card
        key={id}
        className={classes.root}
        variant="outlined"
        onClick={handleCardClick}
      >
        <Grid container style={{ padding: '16px' }}>
          <Grid item xs={2} className={classes.leftSide}>
            <CardMedia style={{ marginBottom: '10px' }}>
              <Avatar className={classes.iconStyled}>
                {withLessonPacksCard && !isSingleSession ? (
                  <ConfirmationNumberOutlinedIcon color="primary" />
                ) : (
                  accessor[category].icon
                )}
              </Avatar>
            </CardMedia>
          </Grid>
          <Grid item xs={10} align="right">
            <Box display="flex" justifyContent="end" alignItems="center">
              <Hidden mdDown implementation="css">
                <ChipsSection
                  status={status}
                  requiresAccessCode={requiresAccessCode}
                  lessonCreditBalanceQuantity={lessonCreditBalanceQuantity}
                  isSingleSession={isSingleSession}
                  withLessonPacksCard={withLessonPacksCard}
                  nextExpirationDate={nextExpirationDate}
                />
              </Hidden>
              {priceInCents && !lessonPacksPrice ? (
                <CardActions disableSpacing className={classes.actionBar}>
                  <Typography variant="subtitle2">
                    ${(priceInCents / 100).toFixed(2)}
                  </Typography>
                </CardActions>
              ) : null}
              {showLessonPackPrice ? (
                <CardActions disableSpacing className={classes.actionBar}>
                  <Typography variant="subtitle2">
                    From ${(lessonPacksPrice / 100).toFixed()} / lesson
                  </Typography>
                </CardActions>
              ) : null}
            </Box>
          </Grid>
          <Grid item xs={12} className={classes.leftSide}>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="caption" className={classes.captionText}>
              {get(locations, '0.name')}
            </Typography>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Typography variant="caption" className={classes.captionText}>
              {durationInMinutes} minutes
            </Typography>
          </Grid>
          {shouldBeClickable && (
            <Grid
              item
              xs={12}
              lg={6}
              align="right"
              className={classes.cardBottom}
            >
              <Hidden lgUp implementation="css">
                <ChipsSection
                  status={status}
                  requiresAccessCode={requiresAccessCode}
                  lessonCreditBalanceQuantity={lessonCreditBalanceQuantity}
                  isSingleSession={isSingleSession}
                  withLessonPacksCard={withLessonPacksCard}
                  nextExpirationDate={nextExpirationDate}
                />
              </Hidden>
              <IconButton
                color="primary"
                style={{ padding: 0, marginLeft: 'auto' }}
                onClick={handleDetailsClick}
              >
                <IconInfoActive fill="#00234B" />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </Card>
      {openDetails && (
        <BookingDetails
          lessonInfo={lessonInfo}
          isOpen={openDetails}
          setIsOpen={setOpenDetails}
          bookingUrl={bookingUrl}
          coachExternalId={coachExternalId}
          coachName={coachName}
          isSingleSession={isSingleSession}
          setOpenVerifyAccessCode={setOpenVerifyAccessCode}
          coachIsViewingOwnSchedule={coachIsViewingOwnSchedule}
        />
      )}
      {openLessonPackDetails && (
        <BookingDetails
          lessonInfo={lessonInfo}
          isOpen={openLessonPackDetails}
          setIsOpen={setOpenLessonPackDetails}
          bookingUrl={bookingUrl}
          coachName={coachName}
          coachExternalId={coachExternalId}
          nextExpirationDate={nextExpirationDate}
          lessonCreditBalanceQuantity={lessonCreditBalanceQuantity}
          isSingleSession={isSingleSession}
          setOpenVerifyAccessCode={setOpenVerifyAccessCode}
          coachIsViewingOwnSchedule={coachIsViewingOwnSchedule}
        />
      )}
      {openVerifyAccessCode && (
        <VerifyAccessCode
          lessonType={lessonInfo}
          isOpen={openVerifyAccessCode}
          setIsOpen={setOpenVerifyAccessCode}
          coachName={coachName}
          setVerifiedAccessCode={setVerifiedAccessCode}
        />
      )}
    </>
  )
}
